import React from "react";
import { toast } from "react-toastify";

import {
  DangerSecondaryButton,
  ErrorDialog,
  PrimaryButton,
  Table,
  TableBody,
  TableHeader,
  TableRow,
  Td,
  Th,
} from "@graffer-inc/console-design-kit";

import type {
  ServiceAccount,
  ServiceAccountKey,
} from "../../../../../../application/domain/service-account/service-account";

type ServiceAccountKeysListProps = {
  serviceAccount: ServiceAccount;
  serviceAccountKeys: ServiceAccountKey[];
  onClickStartCreate: () => void;
  onClickDisableServiceAccountKey?: (
    param: { serviceAccountKeyId: ServiceAccountKey["id"] },
    callback: {
      onSucceeded: () => void;
      onFailed: (errorMessage: string) => void;
    },
  ) => void;
};

export const ServiceAccountKeysList: React.FC<ServiceAccountKeysListProps> = ({
  serviceAccount,
  serviceAccountKeys,
  onClickStartCreate,
  onClickDisableServiceAccountKey,
}) => {
  const [disableTargetKeyId, setDisableTargetKeyId] = React.useState<
    ServiceAccountKey["id"] | undefined
  >();
  const handleProceedDisable = React.useCallback(() => {
    if (!onClickDisableServiceAccountKey || !disableTargetKeyId) {
      return;
    }
    onClickDisableServiceAccountKey(
      { serviceAccountKeyId: disableTargetKeyId },
      {
        onSucceeded: () => {
          toast.success("サービスアカウントキーを無効化しました。");
          setDisableTargetKeyId(undefined);
        },
        onFailed: (message) => {
          toast.error(message);
        },
      },
    );
  }, [onClickDisableServiceAccountKey, disableTargetKeyId]);
  const handleCancelDisable = React.useCallback(() => {
    setDisableTargetKeyId(undefined);
  }, []);

  return (
    <div>
      <nav className="level">
        <div className="level-left"></div>
        <div className="level-right">
          <p className="level-item">
            <PrimaryButton
              text="新しいキーを作成"
              onClick={onClickStartCreate}
              disabled={serviceAccount.disabled}
            />
          </p>
        </div>
      </nav>

      <Table>
        <TableHeader>
          <Th>キーID</Th>
          <Th>名称</Th>
          <Th>有効期限</Th>
          <Th>ステータス</Th>
          {onClickDisableServiceAccountKey && <Th></Th>}
        </TableHeader>
        <TableBody>
          {serviceAccountKeys.map(
            ({ id, name, expiredAtUnixMilliSecond, disabled }) => {
              const statusText = disabled
                ? "無効"
                : Date.now() < expiredAtUnixMilliSecond
                  ? "有効"
                  : "期限切れ";
              return (
                <TableRow key={id}>
                  <Td>{id}</Td>
                  <Td>{name}</Td>
                  <Td>{`${milliSecToDateString(
                    expiredAtUnixMilliSecond,
                  )}${milliSecToTimeString(expiredAtUnixMilliSecond)}`}</Td>
                  <Td>{statusText}</Td>
                  {onClickDisableServiceAccountKey && (
                    <Td>
                      <DangerSecondaryButton
                        text="無効化"
                        disabled={disabled}
                        onClick={setDisableTargetKeyId.bind(null, id)}
                      />
                    </Td>
                  )}
                </TableRow>
              );
            },
          )}
        </TableBody>
      </Table>
      {disableTargetKeyId && (
        <ServiceAccountKeyDisableConfirmModal
          onProceed={handleProceedDisable}
          onCancel={handleCancelDisable}
        />
      )}
    </div>
  );
};

const milliSecToDateString = (milliSec: number): string => {
  const date = new Date(milliSec);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const dateOfMonth = date.getDate();
  return `${year}年${month}月${dateOfMonth}日`;
};

const milliSecToTimeString = (milliSec: number): string => {
  const date = new Date(milliSec);
  const hour = date.getHours();
  const minute = date.getMinutes();
  return `${hour}時${minute}分`;
};

const ServiceAccountKeyDisableConfirmModal: React.FC<{
  onProceed: () => void;
  onCancel: () => void;
}> = ({ onProceed, onCancel }) => {
  return (
    <ErrorDialog
      title="キーを無効化しようとしています"
      proceedText="無効化"
      onProceed={onProceed}
      cancelText="キャンセル"
      onCancel={onCancel}
      contents={
        <div className="container">
          <p>このキーを利用した操作を受け付けなくなります。</p>
          <p>ほかのキーの操作には影響しません。</p>
          <p>この操作は元に戻すことができません。</p>
        </div>
      }
    />
  );
};
